import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import { Download, RotateCcw, Search, Trash2 } from 'lucide-react';
import { formatAuditAction, formatEntityType } from '../utils/formatters';
import PageIntro from '../components/ui/PageIntro';
import Dialog from '../components/common/Dialog';

function formatDate(value) {
  if (!value) return '—';
  try { return new Date(value).toLocaleString('ru-RU'); } catch { return value; }
}

function pretty(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

export default function AdminAuditTrailPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [adminOnlyFilter, setAdminOnlyFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [recycleItems, setRecycleItems] = useState([]);
  const [restoringId, setRestoringId] = useState('');
  const [downloadingEvent, setDownloadingEvent] = useState(false);
  const [eventError, setEventError] = useState('');
  const downloadEvent = async () => {
    setDownloadingEvent(true); setEventError('');
    try {
      const response = await api.get(`/admin/audit-trail/${selected.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data), link = document.createElement('a');
      link.href = url; link.download = `audit-event-${selected.id}.json`; link.click(); URL.revokeObjectURL(url);
    } catch(e) { setEventError(getApiErrorMessage(e)); } finally { setDownloadingEvent(false); }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [rowsRes, summaryRes, recycleRes] = await Promise.all([
        api.get('/admin/audit-trail', { params: { limit: 500 } }),
        api.get('/admin/audit-trail/summary', { params: { days: 30 } }),
        api.get('/admin/recycle-bin'),
      ]);
      setRows(rowsRes.data || []);
      setSummary(summaryRes.data || null);
      setRecycleItems(recycleRes.data || []);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const response = await api.get('/admin/audit-trail/export', { params: { days: 90 }, responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const restoreItem = async (item) => {
    const key = `${item.entity_type}:${item.id}`;
    setRestoringId(key);
    setError('');
    try {
      await api.post(`/admin/recycle-bin/${item.entity_type}/${item.id}/restore`);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setRestoringId('');
    }
  };

  const entityOptions = useMemo(() => {
    const set = new Set(rows.map(r => r.entity_type).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const hay = [row.action, row.entity_type, row.actor?.full_name, row.actor?.username, JSON.stringify(row.payload || {}), JSON.stringify(row.changed_fields || [])].join(' ').toLowerCase();
      const matchesSearch = !q || hay.includes(q);
      const matchesAdminOnly = adminOnlyFilter === 'all' || (adminOnlyFilter === 'yes' ? row.admin_only : !row.admin_only);
      const matchesEntity = entityType === 'all' || row.entity_type === entityType;
      return matchesSearch && matchesAdminOnly && matchesEntity;
    });
  }, [rows, search, adminOnlyFilter, entityType]);

  return (
    <div>
      <PageIntro
        title={uiText("Полный журнал действий")}
        description={uiText("История изменений, сравнение значений по полям и общая аналитика действий в Audit Pro.")}
        aside={(
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline" onClick={exportCsv}><Download size={15} />{uiText(" Экспорт за 90 дней")}</button>
          <button type="button" className="btn btn-outline" onClick={load}>{uiText("Обновить")}</button>
          </div>
        )}
      />

      {summary && (
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card"><div className="stat-value">{summary.events_total}</div><div className="stat-label">{uiText("Событий за 30 дней")}</div></div>
          <div className="stat-card"><div className="stat-value">{summary.by_entity?.[0]?.name || '—'}</div><div className="stat-label">{uiText("Самая активная сущность")}</div></div>
          <div className="stat-card"><div className="stat-value">{summary.by_actor?.[0]?.name || '—'}</div><div className="stat-label">{uiText("Самый активный пользователь")}</div></div>
          <div className="stat-card"><div className="stat-value">{summary.changed_fields?.[0]?.name || '—'}</div><div className="stat-label">{uiText("Самое частое изменённое поле")}</div></div>
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="search-bar" style={{ flex: 1, minWidth: 260 }}>
            <input placeholder={uiText("Поиск по действию, сущности, пользователю, diff…")} value={search} onChange={(e) => setSearch(e.target.value)} />
            <span className="search-bar-icon" aria-hidden="true"><Search /></span>
          </div>
          <select className="form-control" style={{ maxWidth: 220 }} value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            {entityOptions.map(v => <option key={v} value={v}>{v === 'all' ? uiText("Все сущности") : formatEntityType(v)}</option>)}
          </select>
          <select className="form-control" style={{ maxWidth: 220 }} value={adminOnlyFilter} onChange={(e) => setAdminOnlyFilter(e.target.value)}>
            <option value="all">{uiText("Все события")}</option>
            <option value="yes">{uiText("Только административные")}</option>
            <option value="no">{uiText("Обычные события")}</option>
          </select>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? <div className="empty-state">{uiText("Загрузка журнала…")}</div> : error ? <div className="empty-state"><h3>{uiText("Не удалось загрузить журнал")}</h3><p>{error}</p></div> : filtered.length === 0 ? <div className="empty-state"><h3>{uiText("Совпадений нет")}</h3><p>{uiText("Попробуйте изменить фильтры или строку поиска.")}</p></div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>{uiText("Дата")}</th><th>{uiText("Пользователь")}</th><th>{uiText("Действие")}</th><th>{uiText("Сущность")}</th><th>{uiText("Изменения")}</th></tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.id} onClick={() => { setSelected(row); setEventError(''); }} style={{ cursor: 'pointer' }}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.created_at)}</td>
                      <td><div style={{ fontWeight: 600 }}>{row.actor?.full_name || uiText("Система")}</div><div style={{ fontSize: 12, color:'var(--premium-text-secondary)' }}>{row.actor?.username || '—'}</div></td>
                      <td><div style={{ fontWeight: 600 }}>{formatAuditAction(row.action)}</div>{row.admin_only ? <span className="badge badge-red">{uiText("Только администраторы")}</span> : <span className="badge badge-gray">{uiText("Обычное событие")}</span>}</td>
                      <td><div>{formatEntityType(row.entity_type)}</div><div style={{ fontSize: 12, color:'var(--premium-text-secondary)' }}>ID: {row.entity_id ?? '—'}</div></td>
                      <td>
                        {row.changed_fields?.length ? (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {row.changed_fields.slice(0, 4).map((f) => <span key={f} className="badge badge-blue">{f}</span>)}
                            {row.changed_fields.length > 4 && <span className="badge badge-gray">+{row.changed_fields.length - 4}</span>}
                          </div>
                        ) : <span style={{ color:'var(--premium-text-secondary)' }}>{uiText("Без изменений")}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header">
          <div><div className="card-title"><Trash2 size={16} />{uiText(" Корзина")}</div><div className="workspace-muted">{uiText("Удаленные записи сохраняются и могут быть восстановлены администратором.")}</div></div>
          <span className="badge badge-gray">{recycleItems.length}</span>
        </div>
        <div className="card-body" style={{ padding: recycleItems.length ? 0 : undefined }}>
          {recycleItems.length === 0 ? (
            <div className="workspace-empty-state"><div className="workspace-empty-state-title">{uiText("Корзина пуста")}</div><div className="workspace-empty-state-text">{uiText("Удаленные аудиты, шаблоны и аналитические записи появятся здесь.")}</div></div>
          ) : (
            <div className="table-wrap"><table><thead><tr><th>{uiText("Название")}</th><th>{uiText("Тип")}</th><th>{uiText("Удалено")}</th><th /></tr></thead><tbody>
              {recycleItems.map((item) => {
                const key = `${item.entity_type}:${item.id}`;
                return <tr key={key}><td><b>{item.title}</b></td><td>{formatEntityType(item.entity_type)}</td><td>{formatDate(item.deleted_at)}</td><td><button type="button" className="btn btn-outline btn-sm" disabled={restoringId === key} onClick={() => restoreItem(item)}><RotateCcw size={14} /> {restoringId === key ? uiText("Восстановление…") : uiText("Восстановить")}</button></td></tr>;
              })}
            </tbody></table></div>
          )}
        </div>
      </div>

      {selected && (
        <Dialog size="lg" label={uiText("Событие #{v0}", {v0: (selected.id)})} onClose={() => setSelected(null)}>
            <div className="ap-dialog__header">
              <h2 className="ap-dialog__title" data-dialog-heading tabIndex={-1}>{uiText("Событие #")}{selected.id}</h2>
              <button type="button" className="ap-dialog__close" aria-label={uiText("Закрыть")} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="ap-dialog__body">
              {selected.details_truncated && <section className="ap-panel ap-stack"><p>Большое событие показано кратко. Полные данные сохранены без изменений и доступны в JSON.</p><div><button type="button" className="btn btn-outline" disabled={downloadingEvent} onClick={downloadEvent}>{downloadingEvent ? 'Готовим файл…' : 'Скачать полные данные события'}</button></div>{eventError && <p role="alert">{eventError}</p>}</section>}
              <div className="ap-form-grid">
                <section className="ap-panel"><h3 className="ap-panel__heading">{uiText("Метаданные")}</h3><div className="ap-stack">
                  <div><b>{uiText("Дата:")}</b> {formatDate(selected.created_at)}</div>
                  <div><b>{uiText("Действие:")}</b> {formatAuditAction(selected.action)}</div>
                  <div><b>{uiText("Сущность:")}</b> {formatEntityType(selected.entity_type)} #{selected.entity_id ?? '—'}</div>
                  <div><b>{uiText("Пользователь:")}</b> {selected.actor?.full_name || uiText("Система")}</div>
                </div></section>
                <section className="ap-panel"><h3 className="ap-panel__heading">{uiText("Данные события")}</h3><pre className="ap-code-block">{pretty(selected.payload)}</pre></section>
              </div>
              <section className="ap-panel">
                <h3 className="ap-panel__heading">{uiText("Изменения")}</h3>
                  {!selected.changed_fields?.length ? <p className="ap-dialog__description">{uiText("Для этого события нет сравнения по полям.")}</p> : (
                    <div className="ap-table-wrap"><table className="ap-table"><thead><tr><th>{uiText("Поле")}</th><th>{uiText("Было")}</th><th>{uiText("Стало")}</th></tr></thead><tbody>
                      {selected.changed_fields.map((field) => (
                        <tr key={field}>
                          <td style={{ fontWeight: 700 }}>{field}</td>
                          <td><pre className="ap-code-block">{pretty(selected.old_values?.[field])}</pre></td>
                          <td><pre className="ap-code-block">{pretty(selected.new_values?.[field])}</pre></td>
                        </tr>
                      ))}
                    </tbody></table></div>
                  )}
              </section>
            </div>
        </Dialog>
      )}
    </div>
  );
}
